import {
  CalendarOutlined,
  DeleteOutlined,
  FieldTimeOutlined,
  FileTextOutlined
} from "@ant-design/icons"
import {
  Alert,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Select,
  Typography
} from "antd"
import { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { DATE_FORMAT } from "../../../../../../../utils/date/date.ts"

import { BulkActionBar } from "../../../../../../../components/Table/BulkActionBar.tsx"
import type { MilestoneBulkResult } from "../../../../../redux/project-milestones/project-milestones.types.ts"
import { renderMilestoneStatusOption } from "./MilestoneStatusTag.tsx"

const { Text } = Typography

export type MilestoneBulkAction =
  | "change-status"
  | "shift-dates"
  | "set-dates"
  | "delete"

export interface MilestoneBulkActionParams {
  action: MilestoneBulkAction
  ids: string[]
  status?: number
  /** Whole days, negative for earlier. */
  shiftDays?: number
  /** `YYYY-MM-DD`; an absent one leaves that date as it is. */
  startDate?: string
  endDate?: string
}

interface StatusOption {
  label: string
  value: number
}

interface MilestonesBulkActionBarProps {
  selectedIds: string[]
  // A selection survives loading more rows, so a failure can name a milestone
  // whose row is not currently mounted — fall back to the id when it is absent.
  milestoneNames: Record<string, string>
  statusOptions: StatusOption[]
  editAllowed?: boolean
  deleteAllowed?: boolean
  onClear: () => void
  onAction: (params: MilestoneBulkActionParams) => Promise<MilestoneBulkResult>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner follows this
   * rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
  /** The account's date format, typed or picked in the date fields. */
  dateFormat?: string | null
}

type BulkFormValues = {
  status?: number
  shiftDays?: number | null
  startDate?: Dayjs | null
  endDate?: Dayjs | null
}

export function MilestonesBulkResult({
  result,
  milestoneNames
}: {
  result: MilestoneBulkResult
  milestoneNames: Record<string, string>
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("milestoneBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="milestones-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("milestoneBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              {t("milestoneBulk.result.failure", {
                milestone: milestoneNames[failure.id] || failure.id,
                reason: failure.message
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function MilestonesBulkActionBar({
  selectedIds,
  milestoneNames,
  statusOptions,
  editAllowed,
  deleteAllowed,
  onClear,
  onAction,
  onVisibleChange,
  dateFormat
}: MilestonesBulkActionBarProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<MilestoneBulkAction | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<MilestoneBulkResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const actionLabels = useMemo<Record<MilestoneBulkAction, string>>(
    () => ({
      "change-status": t("milestoneBulk.actions.changeStatus"),
      "shift-dates": t("milestoneBulk.actions.shiftDates"),
      "set-dates": t("milestoneBulk.actions.setDates"),
      delete: t("milestoneBulk.actions.delete")
    }),
    [t]
  )

  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: MilestoneBulkAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // Never fan a status change out with nothing to set — the field's own rule
    // covers this, but the request must not depend on the form being connected
    // in time.
    if (activeAction === "change-status" && values.status == null) return
    if (activeAction === "shift-dates" && !values.shiftDays) return
    if (activeAction === "set-dates" && !values.startDate && !values.endDate)
      return

    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: activeAction,
        ids: selectedIds,
        ...(activeAction === "change-status" ? { status: values.status } : {}),
        ...(activeAction === "shift-dates"
          ? { shiftDays: values.shiftDays as number }
          : {}),
        ...(activeAction === "set-dates"
          ? {
              ...(values.startDate
                ? { startDate: values.startDate.format(DATE_FORMAT) }
                : {}),
              ...(values.endDate
                ? { endDate: values.endDate.format(DATE_FORMAT) }
                : {})
            }
          : {})
      })
      setRequestFailed(false)
      setResult(nextResult)
      setActiveAction(null)
      form.resetFields()
    } catch {
      setResult(null)
      setRequestFailed(true)
      setActiveAction(null)
      form.resetFields()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  const visible = !!selectedIds.length || !!result || requestFailed

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const feedback =
    requestFailed || result ? (
      <>
        {requestFailed ? (
          <Alert
            data-testid="milestones-bulk-request-error"
            type="error"
            showIcon
            closable
            onClose={() => setRequestFailed(false)}
            message={t("milestoneBulk.result.requestFailed")}
          />
        ) : null}

        {result ? (
          <Alert
            type={result.failed.length ? "warning" : "success"}
            showIcon
            closable
            onClose={() => setResult(null)}
            message={
              <MilestonesBulkResult
                result={result}
                milestoneNames={milestoneNames}
              />
            }
          />
        ) : null}
      </>
    ) : null

  return (
    <>
      <BulkActionBar
        data-testid="milestones-bulk-action-bar"
        visible
        selectedCount={selectedIds.length}
        selectedLabel={t("milestoneBulk.selected", {
          count: selectedIds.length
        })}
        ariaLabel={t("milestoneBulk.toolbarLabel")}
        actions={
          editAllowed
            ? [
                {
                  key: "change-status",
                  label: actionLabels["change-status"],
                  icon: <FileTextOutlined aria-hidden />,
                  onClick: () => openModal("change-status")
                },
                {
                  key: "shift-dates",
                  label: actionLabels["shift-dates"],
                  icon: <FieldTimeOutlined aria-hidden />,
                  onClick: () => openModal("shift-dates")
                },
                {
                  key: "set-dates",
                  label: actionLabels["set-dates"],
                  icon: <CalendarOutlined aria-hidden />,
                  onClick: () => openModal("set-dates")
                }
              ]
            : []
        }
        destructiveAction={
          deleteAllowed
            ? {
                key: "delete",
                label: actionLabels.delete,
                icon: <DeleteOutlined aria-hidden />,
                onClick: () => openModal("delete")
              }
            : undefined
        }
        onClear={clear}
      >
        {/* Below the toolbar, so a result does not push the controls still in
            use further down the page. */}
        {feedback}
      </BulkActionBar>

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "delete"
            ? t("milestoneBulk.actions.delete")
            : t("milestoneBulk.apply")
        }
        cancelText={t("milestoneBulk.cancel")}
        okButtonProps={{ danger: activeAction === "delete" }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "change-status" ? (
            <Form.Item<BulkFormValues>
              name="status"
              label={t("milestoneBulk.fields.status")}
              rules={[
                {
                  required: true,
                  message: t("milestoneBulk.validation.status")
                }
              ]}
            >
              <Select
                options={statusOptions}
                placeholder={t("milestoneBulk.placeholders.status")}
                optionRender={renderMilestoneStatusOption}
              />
            </Form.Item>
          ) : null}

          {activeAction === "shift-dates" ? (
            <Form.Item<BulkFormValues>
              name="shiftDays"
              label={t("milestoneBulk.fields.shiftDays")}
              extra={t("milestoneBulk.hints.shiftDays")}
              rules={[
                {
                  validator: (_rule, value) =>
                    Number.isInteger(value) && value !== 0
                      ? Promise.resolve()
                      : Promise.reject(
                          new Error(t("milestoneBulk.validation.shiftDays"))
                        )
                }
              ]}
            >
              <InputNumber
                className="w-full"
                precision={0}
                placeholder={t("milestoneBulk.placeholders.shiftDays")}
              />
            </Form.Item>
          ) : null}

          {activeAction === "set-dates" ? (
            <>
              <Form.Item<BulkFormValues>
                name="startDate"
                label={t("milestoneBulk.fields.startDate")}
              >
                <DatePicker
                  className="w-full"
                  format={dateFormat ? [dateFormat, DATE_FORMAT] : DATE_FORMAT}
                />
              </Form.Item>
              <Form.Item<BulkFormValues>
                name="endDate"
                label={t("milestoneBulk.fields.endDate")}
                extra={t("milestoneBulk.hints.setDates")}
                dependencies={["startDate"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_rule, value?: Dayjs | null) => {
                      const start: Dayjs | null | undefined =
                        getFieldValue("startDate")
                      if (!value && !start)
                        return Promise.reject(
                          new Error(t("milestoneBulk.validation.setDates"))
                        )
                      if (value && start && value.isBefore(start, "day"))
                        return Promise.reject(
                          new Error(
                            t("milestoneBulk.validation.endBeforeStart")
                          )
                        )
                      return Promise.resolve()
                    }
                  })
                ]}
              >
                <DatePicker
                  className="w-full"
                  format={dateFormat ? [dateFormat, DATE_FORMAT] : DATE_FORMAT}
                />
              </Form.Item>
            </>
          ) : null}

          {activeAction === "delete" ? (
            <Alert
              type="error"
              showIcon
              message={t("milestoneBulk.deleteImpact", {
                count: selectedIds.length
              })}
              description={t("milestoneBulk.deleteExtraWarning")}
            />
          ) : null}
        </Form>
      </Modal>
    </>
  )
}
